import { useEffect, useState } from 'react';
import { isValidCode, isValidNickname, isValidPin } from '@numslearn/shared';
import { useSettings } from '../settings.js';
import { api, type ClassInfoRow } from '../net/api.js';
import { navigate } from '../router.js';
import type { SessionApi } from '../session/useSession.js';
import type { StringKey } from '../i18n/index.js';

/** Code, name, PIN. Three fields, because a seven-year-old is typing them. */
export function Join({ code, session }: { code: string; session: SessionApi }) {
  const { t } = useSettings();
  const [nickname, setNickname] = useState('');
  const [pin, setPin] = useState('');
  const [entered, setEntered] = useState(code);
  const [info, setInfo] = useState<ClassInfoRow | null>(null);

  // Whose class is this? A child who has typed a code should be told they are
  // about to walk into Miss Fatima's Year 4A, not left to hope.
  useEffect(() => {
    if (!isValidCode(entered)) { setInfo(null); return; }
    let live = true;
    api.get<ClassInfoRow>(`/class/${entered.toUpperCase()}`)
      .then((r) => { if (live) setInfo(r); })
      .catch(() => { if (live) setInfo(null); });
    return () => { live = false; };
  }, [entered]);

  const ready = isValidCode(entered) && isValidNickname(nickname) && isValidPin(pin);
  const busy = session.status === 'joining';

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) return;
    const result = await session.join(entered.toUpperCase(), nickname.trim(), pin);
    if (result.ok) navigate('/play');
  };

  return (
    <form className="join" onSubmit={submit}>
      <h1>{t('join.title', { code: entered || '—' })}</h1>
      {info && (
        <p className="join-who">
          <strong>{t('class.of', { name: info.teacherName })}</strong>
          <span>{info.className}</span>
        </p>
      )}

      {!isValidCode(code) && (
        <label className="field">
          <span>{t('home.code')}</span>
          <input
            value={entered}
            onChange={(e) => setEntered(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6))}
            autoComplete="off" spellCheck={false}
          />
        </label>
      )}

      <label className="field">
        <span>{t('join.nickname')}</span>
        <input
          value={nickname}
          onChange={(e) => setNickname(e.target.value.slice(0, 18))}
          autoComplete="off" spellCheck={false} autoFocus
        />
      </label>

      <label className="field">
        <span>{t('join.pin')}</span>
        <input
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
          inputMode="numeric" autoComplete="off"
          className="pin-input"
        />
        <small>{t('join.pinHint')}</small>
      </label>

      {session.error && (
        <p className="join-err" role="alert">{t(`join.err.${session.error}` as StringKey)}</p>
      )}

      <div className="join-acts">
        <button className="gbtn pri" type="submit" disabled={!ready || busy}>
          {busy ? t('join.joining') : t('join.go')}
        </button>
        <button className="gbtn" type="button" onClick={() => navigate('/')}>{t('nav.home')}</button>
      </div>
    </form>
  );
}
